import { useState, useEffect, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";
import { subscribeActiveTenantContextChanged } from "@/lib/active-tenant-context";
import type { TimelineEntry as CrmTimelineEntry } from "@/hooks/use-crm-timeline";
import type { CrmContactSummarySignal } from "@ease/crm/contact-summary-signals";

const API_URL = import.meta.env.VITE_API_URL ?? "";
const EXPORT_TIMEOUT_MS = 60_000;

// ---------------------------------------------------------------------------
// Types — match api-crm serializer output
// ---------------------------------------------------------------------------

export interface ContactSummary {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  assignedTo?: string | null;
  assignedToLabel?: string | null;
  phone: string | null;
  company: string | null;
  tags: string[];
  source: string | null;
  leadScore: number | null;
  crmSummarySignals?: CrmContactSummarySignal[];
  engagementStatus: string | null;
  lifecycleStage: string | null;
  lastActivityAt: string | null;
  createdAt: string;
}

export interface Contact extends ContactSummary {
  timezone: string | null;
  avatarUrl: string | null;
  sourceDetail: string | null;
  customFields: Record<string, unknown>;
  emailVerifiedAt: string | null;
  updatedAt: string;
}

export interface TimelineEntry extends Partial<CrmTimelineEntry> {
  id: string;
  entityType?: string;
  eventType?: string;
  payload?: Record<string, unknown>;
  actorId?: string | null;
  createdAt?: string;
}

interface ContactListResponse {
  data: {
    items: ContactSummary[];
    nextCursor: string | null;
    total: number;
  };
}

// ---------------------------------------------------------------------------
// Hook: useContactList
// ---------------------------------------------------------------------------

interface UseContactListParams {
  search?: string;
  tag?: string;
  excludeTag?: string;
  limit?: number;
  minScore?: number;
  maxScore?: number;
  createdFrom?: string;
  createdTo?: string;
  lifecycleStage?: string;
  source?: string;
  assignedTo?: string;
  city?: string;
  company?: string;
}

export function useContactList(params: UseContactListParams) {
  const [contacts, setContacts] = useState<ContactSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tenantContextVersion, setTenantContextVersion] = useState(0);

  useEffect(() => subscribeActiveTenantContextChanged(() => {
    setTenantContextVersion((version) => version + 1);
  }), []);

  const fetchContacts = useCallback(async (cursor?: string) => {
    setIsLoading(true);
    setError(null);

    const searchParams = new URLSearchParams();
    if (params.search) searchParams.set("search", params.search);
    if (params.tag) searchParams.set("tag", params.tag);
    if (params.excludeTag) searchParams.set("excludeTag", params.excludeTag);
    if (params.limit) searchParams.set("limit", String(params.limit));
    if (params.minScore != null) searchParams.set("minScore", String(params.minScore));
    if (params.maxScore != null) searchParams.set("maxScore", String(params.maxScore));
    if (params.createdFrom) searchParams.set("createdFrom", params.createdFrom);
    if (params.createdTo) searchParams.set("createdTo", params.createdTo);
    if (params.lifecycleStage) searchParams.set("lifecycleStage", params.lifecycleStage);
    if (params.source) searchParams.set("source", params.source);
    if (params.assignedTo) searchParams.set("assignedTo", params.assignedTo);
    if (params.city) searchParams.set("city", params.city);
    if (params.company) searchParams.set("company", params.company);
    if (cursor) searchParams.set("cursor", cursor);

    const qs = searchParams.toString();
    const path = `/crm/people${qs ? `?${qs}` : ""}`;

    try {
      const res = await apiFetch<ContactListResponse>(path);
      if (cursor) {
        setContacts((prev) => [...prev, ...res.data.items]);
      } else {
        setContacts(res.data.items);
      }
      setNextCursor(res.data.nextCursor);
      setTotal(res.data.total);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load contacts");
    } finally {
      setIsLoading(false);
    }
  }, [
    params.createdFrom,
    params.createdTo,
    params.excludeTag,
    params.assignedTo,
    params.city,
    params.company,
    params.lifecycleStage,
    params.limit,
    params.maxScore,
    params.minScore,
    params.search,
    params.source,
    params.tag,
    tenantContextVersion,
  ]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const loadMore = useCallback(() => {
    if (nextCursor) fetchContacts(nextCursor);
  }, [nextCursor, fetchContacts]);

  return { contacts, total, nextCursor, isLoading, error, loadMore, refetch: () => fetchContacts() };
}

// ---------------------------------------------------------------------------
// Hook: useContact (single)
// ---------------------------------------------------------------------------

export function useContact(id: string | null) {
  const [contact, setContact] = useState<Contact | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!id) {
      setContact(null);
      setIsLoading(false);
      setError(null);
      return () => {
        cancelled = true;
      };
    }

    setIsLoading(true);
    setError(null);

    apiFetch<{ data: Contact }>(`/crm/people/${id}`)
      .then((res) => {
        if (!cancelled) setContact(res.data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Failed to load contact");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  return { contact, isLoading, error };
}

// ---------------------------------------------------------------------------
// Function: createContact — POST /crm/people
// ---------------------------------------------------------------------------

export async function createContact(data: {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  tags?: string[];
  source?: string;
  notes?: string;
}): Promise<Contact> {
  const res = await apiFetch<{ data: Contact }>("/crm/people", {
    method: "POST",
    body: JSON.stringify(buildCreateContactPayload(data)),
  });
  return res.data;
}

interface CreateContactPayload {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  tags?: string[];
  source?: string;
  customFields?: Record<string, unknown>;
}

function trimmedValue(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function buildCreateContactPayload(data: {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  tags?: string[];
  source?: string;
  notes?: string;
}): CreateContactPayload {
  const firstName = trimmedValue(data.firstName);
  const lastName = trimmedValue(data.lastName);
  const phone = trimmedValue(data.phone);
  const source = trimmedValue(data.source);
  const tags = data.tags?.map((tag) => tag.trim()).filter(Boolean);
  const company = trimmedValue(data.company);
  const notes = trimmedValue(data.notes);
  const customFields = {
    ...(company ? { company } : {}),
    ...(notes ? { notes } : {}),
  };

  return {
    email: data.email.trim(),
    ...(firstName ? { firstName } : {}),
    ...(lastName ? { lastName } : {}),
    ...(phone ? { phone } : {}),
    ...(tags && tags.length > 0 ? { tags } : {}),
    ...(source ? { source } : {}),
    ...(Object.keys(customFields).length > 0 ? { customFields } : {}),
  };
}

// ---------------------------------------------------------------------------
// Function: updateContact — PATCH /crm/people/:id
// ---------------------------------------------------------------------------

export async function updateContact(id: string, data: Partial<{
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  tags: string[];
  source: string;
}>): Promise<Contact> {
  const res = await apiFetch<{ data: Contact }>(`/crm/people/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
  return res.data;
}

// ---------------------------------------------------------------------------
// Function: deleteContact — DELETE /crm/people/:id
// ---------------------------------------------------------------------------

export async function deleteContact(id: string): Promise<void> {
  await apiFetch(`/crm/people/${id}`, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// Hook: useContactTimeline
// ---------------------------------------------------------------------------

export function useContactTimeline(contactId: string | null) {
  const [entries, setEntries] = useState<TimelineEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!contactId) { setEntries([]); return; }

    setIsLoading(true);
    apiFetch<{ data: TimelineEntry[] }>(buildContactTimelinePath(contactId))
      .then((res) => setEntries(res.data))
      .catch(() => setEntries([]))
      .finally(() => setIsLoading(false));
  }, [contactId]);

  return { entries, isLoading };
}

export function buildContactTimelinePath(contactId: string) {
  return `/crm/intelligence/contacts/${contactId}/timeline`;
}

export function useContactSources() {
  const [sources, setSources] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);

    apiFetch<{ data: string[] }>("/crm/people/sources")
      .then((res) => setSources(res.data ?? []))
      .catch((err) => setError(err instanceof ApiError ? err.message : "Failed to load contact sources"))
      .finally(() => setIsLoading(false));
  }, []);

  return { sources, isLoading, error };
}

export async function exportAllContactsCsv(): Promise<Blob> {
  const response = await fetch(`${API_URL}/crm/people/export`, {
    credentials: "include",
    signal: AbortSignal.timeout(EXPORT_TIMEOUT_MS),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: "Export failed" }));
    const parsed = body as { error?: string; code?: string };
    throw new ApiError(response.status, parsed.error ?? "Export failed", "/crm/people/export", parsed.code);
  }

  return response.blob();
}
